import React, { FC, useEffect, useState } from 'react';
import zxcvbn from 'zxcvbn';

interface PasswordStrengthBarProps {
    password: string;
    className?: string;
    maxLength?: number;
}

export const PasswordStrengthBar: FC<PasswordStrengthBarProps> = ({
    password,
    className,
    maxLength = 100,
}) => {
    const [feedback, setFeedback] = useState<string | null>(
        'Enter a new password',
    );
    const [score, setScore] = useState<number>(0);

    const minLength = 8;
    const specialCharPattern = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/;
    const numberPattern = /\d/;
    const upperCasePattern = /[A-Z]/;
    const lowerCasePattern = /[a-z]/;

    const meetsRequirements = (password: string) => {
        const hasMinLength = password.length >= minLength;
        const hasSpecialChar = specialCharPattern.test(password);
        const hasNumber = numberPattern.test(password);
        const hasUpperCase = upperCasePattern.test(password);
        const hasLowerCase = lowerCasePattern.test(password);

        return (
            hasMinLength &&
            hasSpecialChar &&
            hasNumber &&
            hasUpperCase &&
            hasLowerCase
        );
    };

    const customFeedback = (score: number, password: string) => {
        if (password.length === 0) return 'Enter a new password';

        if (password.length > maxLength) return 'Too long';

        if (password.length < minLength) return 'Too short';

        if (!meetsRequirements(password)) {
            if (!numberPattern.test(password)) return 'Must include a number';
            if (!upperCasePattern.test(password))
                return 'Must include an uppercase letter';
            if (!lowerCasePattern.test(password))
                return 'Must include a lowercase letter';
            if (!specialCharPattern.test(password))
                return 'Must include a special character (@, &, *, etc.)';
        }

        switch (score) {
            case 0:
                return 'Too weak';
            case 1:
                return 'Too weak';
            case 2:
                return 'Weak';
            case 3:
                return 'Strong';
            case 4:
                return 'Very strong';
            default:
                return null;
        }
    };

    const evaluatePassword = (password: string) => {
        const result = zxcvbn(password);
        const customMsg = customFeedback(result.score, password);

        setScore(result.score);
        setFeedback(customMsg);
    };

    const paintBarColors = (): string[] => {
        if (password.length === 0) {
            return ['#e0e0e0', '#e0e0e0', '#e0e0e0'];
        }
        if (password.length > maxLength) {
            return ['#e46447', '#e46447', '#e46447'];
        }
        if (password.length < minLength) {
            return ['#e46447', '#e0e0e0', '#e0e0e0'];
        }
        if (!meetsRequirements(password)) {
            return ['#e46447', '#e0e0e0', '#e0e0e0'];
        }
        if (score === 0 || score === 1) {
            return ['#e46447', '#e0e0e0', '#e0e0e0'];
        } else if (score === 2) {
            return ['#f99e00', '#f99e00', '#e0e0e0'];
        } else if (score >= 3) {
            return ['#10b5a8', '#10b5a8', '#10b5a8'];
        }
        return ['#e46447', '#e46447', '#e46447'];
    };

    const bars = paintBarColors();

    useEffect(() => {
        evaluatePassword(password);
    }, [password]);

    return (
        <div className={className}>
            <div>
                <div className="flex space-x-1">
                    {bars.map((color, index) => (
                        <div
                            key={index}
                            className="h-1 w-1/3 rounded my-2"
                            style={{ backgroundColor: color }}
                        />
                    ))}
                </div>
            </div>
            {feedback && <p className="text-sm text-brand-ocean">{feedback}</p>}
        </div>
    );
};
